import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import type { Section } from '@omni/ai/features/card.schema';

/** A `progress` section's ladder steps. */
type ProgressStep = NonNullable<Extract<Section, { type: 'progress' }>['steps']>[number];

/** One rung in the ladder, with positional state. */
interface LadderRung {
  step: ProgressStep;
  /** True if this rung is at or below the current stage (achieved). */
  filled: boolean;
  /** True when the connector line below this rung links two filled rungs. */
  connectorFilled: boolean;
}

/** One row in the bars variant. */
export interface ProgressBar {
  label: string;
  percent: number;
  detail: string | null;
  display: string | null;
}

/**
 * `progress` section, three variants:
 *  - `ring`   — radial 0-100 percent.
 *  - `ladder` — ordered stepped scale (one current rung).
 *  - `bars`   — vertical list of labelled linear progress bars.
 * The renderer keys strictly off `variant` and ignores the non-matching sibling
 * fields — a `ring` ignores stray `steps`/`bars`, etc.
 */
@Component({
  selector: 'card-progress',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './card-progress.component.html',
  styleUrls: ['./card-progress.component.scss'],
})
export class CardProgressComponent {
  @Input() variant: 'ring' | 'ladder' | 'bars' = 'ring';
  @Input() title: string | null = null;
  @Input() label: string | null = null;
  @Input() percent: number | null = null;
  @Input() detail: string | null = null;
  @Input() steps: ProgressStep[] | null = null;
  @Input() bars: ProgressBar[] | null = null;

  /** Per-instance id prefix so SVG gradient ids stay unique across rings. */
  readonly uid = Math.random().toString(36).slice(2, 9);
  readonly #translate = inject(TranslateService);

  get safePercent(): number {
    return this.percent ?? 0;
  }

  /**
   * True when any bar carries a detail. Drives a reserved detail column so every
   * bar's track ends at the same x — bars stay comparable instead of one row's
   * track shrinking just because it has a "8 of 10" suffix and its siblings don't.
   */
  get anyBarDetail(): boolean {
    return !!this.bars?.some(b => !!this.barText(b));
  }

  /** Right-side label for a bar — explicit `detail`, else the agent's `display`. */
  barText(bar: ProgressBar): string | null {
    return bar.detail ?? bar.display;
  }

  // ---- ring helpers --------------------------------------------------------

  /** SVG stroke-dasharray for a radial progress ring. r=30 → circumference ≈ 188.5. */
  ringDasharray(percent: number): string {
    const r = 30;
    const circ = 2 * Math.PI * r;
    const filled = (Math.min(Math.max(percent, 0), 100) / 100) * circ;
    return `${filled.toFixed(2)} ${(circ - filled).toFixed(2)}`;
  }

  ringAriaLabel(label: string | null, percent: number, detail: string | null): string {
    const parts = [`${label ?? this.#translate.instant('NOVA_CHART_PROGRESS')}: ${percent}%`];
    if (detail) parts.push(detail);
    return parts.join('. ');
  }

  // ---- ladder helpers ------------------------------------------------------

  ladderRungs(steps: ProgressStep[]): LadderRung[] {
    const currentIdx = steps.findIndex(s => s.current);
    const filled = steps.map((_, i) => currentIdx >= 0 && i >= currentIdx);
    return steps.map((step, i) => ({
      step,
      filled: filled[i],
      connectorFilled: filled[i] && (i + 1 < filled.length ? filled[i + 1] : false),
    }));
  }

  // ---- bars helpers -----------------------------------------------------------

  /** Clamps a raw percent value to [0, 100]. */
  clampPercent(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }

  barAriaLabel(bar: ProgressBar): string {
    const parts = [`${bar.label}: ${this.clampPercent(bar.percent)}%`];
    const text = this.barText(bar);
    if (text) parts.push(text);
    return parts.join('. ');
  }
}
